import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Leaf, ShieldCheck, Award, Sparkles, ArrowRight, Star } from "lucide-react";
import { http } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import DoshaQuiz from "@/components/DoshaQuiz";

const CATEGORY_IMAGES = {
  "herbal-supplements": "https://images.unsplash.com/photo-1701520839071-55bdfe64c5ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxvcmdhbmljJTIwaGVyYmFsJTIwdGVhJTIwc3VwcGxlbWVudHMlMjBza2luJTIwY2FyZSUyMGhlYWx0aCUyMG5hdHVyYWwlMjBwcm9kdWN0c3xlbnwwfHx8fDE3ODgyNDkxOTl8MA&ixlib=rb-4.1.0&q=85",
  "personal-care": "https://images.unsplash.com/photo-1787878513774-662084c59582?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxvcmdhbmljJTIwZXNzZW50aWFsJTIwb2lscyUyMG5hdHVyYWwlMjBjb3NtZXRpYyUyMHNlcnVtJTIwYm90dGxlfGVufDB8fHx8MTc4ODI0OTIwOHww&ixlib=rb-4.1.0&q=85",
  "immunity-boosters": "https://images.unsplash.com/photo-1716972065552-e8ed1216ce98?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxvcmdhbmljJTIwaGVyYmFsJTIwdGVhJTIwc3VwcGxlbWVudHMlMjBza2luJTIwY2FyZSUyMGhlYWx0aCUyMG5hdHVyYWwlMjBwcm9kdWN0c3xlbnwwfHx8fDE3ODgyNDkxOTl8MA&ixlib=rb-4.1.0&q=85",
  "organic-teas": "https://images.unsplash.com/photo-1514733670139-4d87a1941d55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxvcmdhbmljJTIwaGVyYmFsJTIwdGVhJTIwc3VwcGxlbWVudHMlMjBza2luJTIwY2FyZSUyMGhlYWx0aCUyMG5hdHVyYWwlMjBwcm9kdWN0c3xlbnwwfHx8fDE3ODgyNDkxOTl8MA&ixlib=rb-4.1.0&q=85",
  "essential-oils": "https://images.unsplash.com/photo-1676852148076-7a92002419f3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxvcmdhbmljJTIwZXNzZW50aWFsJTIwb2lscyUyMG5hdHVyYWwlMjBjb3NtZXRpYyUyMHNlcnVtJTIwYm90dGxlfGVufDB8fHx8MTc4ODI0OTIwOHww&ixlib=rb-4.1.0&q=85",
  "wellness-foods": "https://images.unsplash.com/photo-1784902008628-64c816f9f39a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY6OTF8MHwxfHNlYXJjaHwxfHxheXVydmVkaWMlMjBoZXJiYWwlMjB3ZWxsbmVzcyUyMG9yZ2FuaWMlMjBwcm9kdWN0cyUyMGJvdHRsZXMlMjBvaWxzJTIwbmF0dXJhbCUyMGhlcm98ZW58MHx8fHwxNzg4MjQ5MTk5fDA&ixlib=rb-4.1.0&q=85",
  "indian-masala": "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [quizOpen, setQuizOpen] = useState(false);

  useEffect(() => {
    http.get("/products").then((r) => setProducts(r.data.slice(0, 8)));
    http.get("/categories").then((r) => setCategories(r.data));
    http.get("/vendors?status=approved").then((r) => setVendors(r.data));
  }, []);

  return (
    <div className="grain">
      <DoshaQuiz open={quizOpen} onClose={() => setQuizOpen(false)} />
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20">
          <div className="grid gap-10 lg:grid-cols-12 items-center">
            <div className="lg:col-span-7 fade-slide-up">
              <span className="badge-sage inline-flex items-center gap-1.5" data-testid="hero-badge">
                <ShieldCheck className="w-3.5 h-3.5" /> Curated · Verified · Marketing-First Marketplace
              </span>
              <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A201C] leading-[1.05] tracking-tight">
                Pure Ayurvedic wisdom, <br />
                <span className="text-[#1E3A2B] italic">verified organic wellness</span>
                <span className="text-[#D97706]">.</span>
              </h1>
              <p className="mt-6 text-lg text-[#5A6B5D] leading-relaxed max-w-xl">
                Healthion India is a curated marketplace by Amiroop Healthion LLP. We don't manufacture — we <strong>discover, verify and market</strong> authentic Ayurvedic formulations from licensed Indian herbal artisans & doctors, tailored to your Dosha.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/products" className="btn-forest inline-flex items-center gap-2" data-testid="hero-cta-explore">
                  Explore Approved Products <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/dosha-quiz"
                  className="btn-outline-forest inline-flex items-center gap-2"
                  data-testid="hero-cta-dosha-quiz"
                >
                  <Sparkles className="w-4 h-4" /> Take Dosha Quiz
                </Link>
              </div>
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
                {[
                  { icon: ShieldCheck, label: "Vendor KYC Verified" },
                  { icon: Award, label: "AYUSH Licensed Sellers" },
                  { icon: Leaf, label: "Direct from Artisans" },
                  { icon: Sparkles, label: "Dosha Guidance" },
                ].map((t, i) => (
                  <div key={i} className="text-xs text-[#5A6B5D] flex items-center gap-2">
                    <t.icon className="w-4 h-4 text-[#1E3A2B]" /> {t.label}
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 fade-slide-up stagger-2">
              <div className="relative">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-[#E2DDD5]">
                  <img
                    src="https://images.unsplash.com/photo-1784902008628-64c816f9f39a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY6OTF8MHwxfHNlYXJjaHwxfHxheXVydmVkaWMlMjBoZXJiYWwlMjB3ZWxsbmVzcyUyMG9yZ2FuaWMlMjBwcm9kdWN0cyUyMGJvdHRsZXMlMjBvaWxzJTIwbmF0dXJhbCUyMGhlcm98ZW58MHx8fHwxNzg4MjQ5MTk5fDA&ixlib=rb-4.1.0&q=85"
                    alt="Ayurvedic products"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-4 bg-white border border-[#E2DDD5] rounded-2xl p-4 shadow-xl max-w-[220px]">
                  <div className="flex items-center gap-1 text-[#D97706]">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}
                  </div>
                  <p className="text-xs text-[#1A201C] mt-1 font-semibold">4.8 average from 8,240 verified customers</p>
                </div>
                <div className="absolute -top-4 -right-2 bg-[#1E3A2B] text-white rounded-2xl p-4 shadow-xl">
                  <div className="text-[10px] font-mono opacity-70 uppercase">Vendors</div>
                  <div className="font-serif text-3xl font-bold">120+</div>
                  <div className="text-[10px] opacity-70">Verified Indian brands</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs font-mono text-[#D97706] uppercase tracking-wider">Curated collections</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A201C] mt-1">Shop by wellness intent</h2>
          </div>
          <Link to="/products" className="hidden sm:inline text-sm text-[#1E3A2B] font-semibold hover:text-[#D97706]">View all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {categories.map((c) => (
            <Link key={c.slug} to={`/products?category=${c.slug}`} className="card-warm overflow-hidden group" data-testid={`category-${c.slug}`}>
              <div className="aspect-square bg-[#E8F0EA] overflow-hidden">
                <img src={CATEGORY_IMAGES[c.slug]} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-3">
                <div className="font-serif text-base font-semibold text-[#1A201C] leading-tight">{c.name}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* DOSHA QUIZ PROMO BAND */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1E3A2B] via-[#254a37] to-[#1E3A2B] p-8 sm:p-12">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#D97706]/25 blur-3xl" />
          <div className="absolute -left-20 -bottom-24 w-72 h-72 rounded-full bg-[#E8F0EA]/10 blur-3xl" />
          <div className="relative grid gap-6 lg:grid-cols-[1fr_auto] items-center">
            <div>
              <div className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-[#D97706]">
                <Sparkles className="w-3.5 h-3.5" /> Doctor-guided · Personalised
              </div>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FAF8F5] leading-tight">
                Not sure where to start?<br />
                <span className="italic text-[#E8F0EA]">Discover your Dosha in 90 seconds.</span>
              </h2>
              <p className="mt-3 text-sm sm:text-base text-[#FAF8F5]/80 max-w-2xl leading-relaxed">
                An 8-question quiz designed by Dr. Rupali Srivastava (Dietitian &amp; Nutritionist). Get a personalised note from the founder and curated Ayurvedic picks that match your true constitution.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Link
                to="/dosha-quiz"
                className="btn-amber inline-flex items-center gap-2 whitespace-nowrap"
                data-testid="promo-cta-dosha-quiz"
              >
                <Sparkles className="w-4 h-4" /> Start Dosha Quiz
              </Link>
              <span className="text-[10px] font-mono text-[#FAF8F5]/50 text-center">Free · No account needed</span>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs font-mono text-[#D97706] uppercase tracking-wider">Doctor-approved bestsellers</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A201C] mt-1">Featured Rasayana</h2>
          </div>
          <Link to="/products" className="text-sm text-[#1E3A2B] font-semibold hover:text-[#D97706]">Browse all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* CONSULT A DOCTOR CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="rounded-3xl bg-gradient-to-br from-[#E8F0EA] to-[#FAF8F5] border border-[#1E3A2B]/15 p-8 sm:p-12 grid gap-8 md:grid-cols-2 items-center">
          <div>
            <span className="badge-sage inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Verified BAMS Practitioners</span>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-bold text-[#1A201C] tracking-tight">
              Consult a licensed Ayurvedic doctor
            </h2>
            <p className="mt-3 text-[#5A6B5D] max-w-lg">
              Private 30-minute sessions over video, phone or in-clinic with BAMS-registered practitioners.
              Verified credentials, transparent fees, and moderated by Amiroop Healthion LLP.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/practitioners" className="btn-forest inline-flex items-center gap-2" data-testid="home-consult-cta">
                Browse Doctors <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/practitioner/join" className="btn-outline-forest inline-flex items-center gap-2" data-testid="home-join-practitioner-cta">
                Join as Practitioner
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { title: "Verified doctors", body: "Every practitioner passes NCISM registration checks." },
              { title: "Video · Phone · In-clinic", body: "Choose the format that suits you best." },
              { title: "Transparent pricing", body: "See the doctor's fee upfront. No hidden charges." },
            ].map((c) => (
              <div key={c.title} className="bg-white/70 backdrop-blur border border-[#E2DDD5] rounded-2xl p-4">
                <div className="font-serif text-lg font-semibold text-[#1E3A2B]">{c.title}</div>
                <div className="mt-1 text-xs text-[#5A6B5D]">{c.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid gap-10 lg:grid-cols-[1fr_2fr] items-start">
          <div>
            <div className="text-xs font-mono text-[#D97706] uppercase tracking-wider">Community reviews</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1A201C] mt-1">Trusted by 8,240+ Ayurveda families</h2>
            <p className="text-[#5A6B5D] mt-3 text-sm leading-relaxed">
              Every review on Healthion India is from a verified purchase. No paid placements. No filler stars.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { name: "Anjali M.", city: "Bengaluru", stars: 5, quote: "I finally understand my Pitta — and the products actually match. The Kumkumadi serum has been a game-changer for my skin.", tag: "Verified Buyer" },
              { name: "Rohan S.", city: "Mumbai", stars: 5, quote: "Love that Amiroop Healthion validates every vendor. I trust the sourcing more than any other marketplace I've tried.", tag: "3x Repeat Customer" },
              { name: "Meera V.", city: "Kochi", stars: 5, quote: "Ordered the Chyawanprash for my parents. Real ghee, real Amla — you can taste the difference from mass-market brands.", tag: "Verified Buyer" },
              { name: "Karan D.", city: "Delhi NCR", stars: 4, quote: "Ashwagandha helped my sleep within two weeks. Delivery was quick and packaging is thoughtful.", tag: "Verified Buyer" },
            ].map((r, i) => (
              <div key={i} className="card-warm p-5">
                <div className="flex items-center gap-1 text-[#D97706]">
                  {[...Array(5)].map((_, s) => <Star key={s} className={`w-3.5 h-3.5 ${s < r.stars ? "fill-current" : ""}`} />)}
                </div>
                <p className="mt-3 text-sm text-[#1A201C] leading-relaxed">"{r.quote}"</p>
                <div className="mt-4 flex items-center justify-between text-xs">
                  <div>
                    <div className="font-semibold text-[#1A201C]">{r.name}</div>
                    <div className="text-[#5A6B5D] font-mono">{r.city}</div>
                  </div>
                  <span className="badge-sage">{r.tag}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VENDORS */}
      <section className="bg-[#1E3A2B] text-[#FAF8F5] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-2 items-center">
          <div>
            <div className="text-xs font-mono text-[#D97706] uppercase tracking-wider">Verified vendor collective</div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mt-2">Independent Indian brands, one trusted marketplace.</h2>
            <p className="mt-4 opacity-80 leading-relaxed">
              Amiroop Healthion LLP is a marketing marketplace only — we do not manufacture. Every vendor listed is manually verified for GST, FSSAI, AYUSH license, and product lab reports before going live.
            </p>
            <Link to="/vendor/register" className="mt-6 inline-flex btn-amber" data-testid="vendors-cta-register">
              Apply as Vendor →
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {vendors.slice(0, 4).map((v) => (
              <div key={v.id} className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <div className="text-xs font-mono opacity-60">VENDOR · AYUSH VERIFIED</div>
                <div className="font-serif text-xl font-semibold mt-1">{v.business_name}</div>
                <div className="text-xs opacity-70 mt-1">{v.address}</div>
                <div className="mt-3 text-[10px] font-mono opacity-60">GSTIN {v.gstin}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
